import type { NavigateFunction } from '@remix-run/react';
import {
  clearGeneralContentHookReturnPath,
  getGeneralContentHookReturnPath,
} from '~/services/hooks/general-content-screen-hook';
import { ActionResult } from '../enums';
import type { ActionContext, ActionHandler } from '../types';

type LoaderScreensData = {
  screens?: Array<{
    home?: boolean;
    general?: { pathname?: string };
  }>;
};

/**
 * Resolves the home screen path from loader data.
 *
 * @param data - Loader data attached to the action context.
 * @returns Home screen pathname when configured.
 */
function getHomeScreenPath(data: unknown): string {
  const screens = (data as LoaderScreensData | undefined)?.screens;
  if (!Array.isArray(screens)) return '/';

  const homeScreen = screens.find((screen) => screen.home);
  return homeScreen?.general?.pathname || '/';
}

/**
 * Resolves the navigation target for finishHook.
 * Returns the screen that triggered the hook, or home when unavailable.
 *
 * @param ctx - Runtime action context.
 * @returns Path to navigate to after the hook screen completes.
 */
function resolveFinishHookPath(ctx: ActionContext): string {
  return getGeneralContentHookReturnPath() || getHomeScreenPath(ctx.data);
}

/**
 * finishHookHandler
 * Completes a hook screen flow by navigating back to the entry screen.
 */
export const finishHookHandler: ActionHandler = (
  _action,
  ctx: ActionContext & { navigate?: NavigateFunction }
) => {
  const targetPath = resolveFinishHookPath(ctx);

  clearGeneralContentHookReturnPath();

  const navigate = ctx.navigate;
  if (navigate) {
    navigate(targetPath, { replace: true });
    return ActionResult.Success;
  }

  if (typeof window !== 'undefined') {
    window.location.assign(targetPath);
    return ActionResult.Success;
  }

  return ActionResult.Error;
};

/**
 * goHomeHandler
 * Exits hook flow and navigates to the app home screen.
 */
export const goHomeHandler: ActionHandler = (
  _action,
  ctx: ActionContext & { navigate?: NavigateFunction }
) => {
  const targetPath = getHomeScreenPath(ctx.data);

  clearGeneralContentHookReturnPath();

  const navigate = ctx.navigate;
  if (navigate) {
    navigate(targetPath, { replace: true });
    return ActionResult.Success;
  }

  if (typeof window !== 'undefined') {
    window.location.assign(targetPath);
    return ActionResult.Success;
  }

  return ActionResult.Error;
};
